/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useMemo, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Swiper, SwiperSlide} from '../swiper'
import {Navigation} from 'swiper/modules'

import * as customStyles from './styles.module.css'

// Chakra Components
import {
    AspectRatio,
    Box,
    Flex,
    Skeleton as ChakraSkeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

const LARGE = 'large'
const SMALL = 'small'

/**
 * The skeleton representation of the image gallery component. Use this component while
 * you are waiting for product data to be returnd from the server.
 */

export const Skeleton = ({size}) => {
    const styles = useMultiStyleConfig('ImageGallery', {size})

    return (
        <Box data-testid="sf-image-gallery-skeleton">
            <Flex flexDirection="column">
                <AspectRatio ratio={1} {...styles.heroImageSkeleton}>
                    <ChakraSkeleton />
                </AspectRatio>
                <Flex>
                    {new Array(4).fill(0).map((_, index) => (
                        <AspectRatio ratio={1} {...styles.thumbnailImageSkeleton} key={index}>
                            <ChakraSkeleton />
                        </AspectRatio>
                    ))}
                </Flex>
            </Flex>
        </Box>
    )
}

Skeleton.propTypes = {
    size: PropTypes.bool
}

/**
 * The image gallery displays a hero image and thumbnails below it. You can control which
 * image groups that are use by passing in the current selected variation values.
 */
export const ModalImageGallery = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    size,
    lazy = false
}) => {
    const [selectedIndex, setSelectedIndex] = useState(0)
    const styles = useMultiStyleConfig('ImageGallery', {size})

    // Get the 'hero' images for the current variation to mount the carousel
    const heroImageGroup = useMemo(
        () =>
            findImageGroupBy(imageGroups, {
                viewType: LARGE,
                selectedVariationAttributes
            }),
        [selectedVariationAttributes]
    )

    useEffect(() => {
        // reset the selected index when location search changes
        setSelectedIndex(0)
    }, [])

    const loadingStrategy = lazy ? 'lazy' : 'eager'

    const heroImageMaxWidth = styles.heroImage.maxWidth[3] // in px

    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const PRODUCT_IMGS_RESIZE_PARAMS_ENABLED = getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', site?.id) == 'true'

    // Show skeleton while images are loading
    if (!heroImageGroup?.images?.length) {
        return (
            <Box w="100%" maxW={['100%', '100%', '100%', '310px']} overflow="hidden">
                {/* Mobile (<769px): show multiple skeleton tiles like the swiper (2.3 per view) */}
                <Flex display={{base: 'flex', md: 'none'}} gap={2}>
                    <Box flex="0 0 calc(100% / 2.3 - 6px)">
                        <AspectRatio ratio={2 / 3} w="100%">
                            <ChakraSkeleton />
                        </AspectRatio>
                    </Box>
                    <Box flex="0 0 calc(100% / 2.3 - 6px)">
                        <AspectRatio ratio={2 / 3} w="100%">
                            <ChakraSkeleton />
                        </AspectRatio>
                    </Box>
                    <Box flex="0 0 calc(100% / 2.3 - 6px)">
                        <AspectRatio ratio={2 / 3} w="100%">
                            <ChakraSkeleton />
                        </AspectRatio>
                    </Box>
                </Flex>
                {/* Desktop (>=769px): single skeleton */}
                <Box display={{base: 'none', md: 'block'}} w="310px">
                    <AspectRatio ratio={2 / 3} w="100%">
                        <ChakraSkeleton />
                    </AspectRatio>
                </Box>
            </Box>
        )
    }

    return (
        <Box maxW={['100%', '100%', '100%', '310px', '310px', '310px']}>
            {heroImageGroup.images.length > 1 ? (
                <Swiper
                    className={customStyles.carouselWrapper}
                    modules={[Navigation]}
                    spaceBetween={8}
                    slidesPerView={2.3}
                    navigation
                    breakpoints={{
                        769: {
                            slidesPerView: 1
                        }
                    }}
                >
                    {heroImageGroup.images.map((image, index) => (
                        <SwiperSlide key={index}>
                            <DynamicImage
                                src={PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
                                    ? `${image.disBaseLink || image.link}[?sw={width}&q=60]`
                                    : image.disBaseLink || image.link}
                                widths={{
                                    base: '100vw',
                                    lg: heroImageMaxWidth
                                }}
                                imageProps={{
                                    alt: image.alt
                                }}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            ) : (
                <Box>
                    <DynamicImage
                        src={PRODUCT_IMGS_RESIZE_PARAMS_ENABLED
                            ? `${heroImageGroup.images[0].disBaseLink || heroImageGroup.images[0].link}[?sw={width}&q=60]`
                            : heroImageGroup.images[0].disBaseLink || heroImageGroup.images[0].link}
                        widths={{
                            base: '100vw',
                            lg: heroImageMaxWidth
                        }}
                        imageProps={{
                            alt: heroImageGroup.images[0].alt,
                            loading: loadingStrategy
                        }}
                    />
                </Box>
            )}
        </Box>
    )
}

ModalImageGallery.propTypes = {
    /**
     * The images array to be rendered
     */
    imageGroups: PropTypes.array,
    /**
     * The current selected variation values
     */
    selectedVariationAttributes: PropTypes.object,
    /**
     * Size of the Image gallery, this will be used to determined the max width from styles
     */
    size: PropTypes.string,
    /**
     * Determines whether the image will be lazy loaded or not
     */
    lazy: PropTypes.bool
}
